<template>
    <div class="flex flex-col ">
        <div class="flex justify-between items-center mt-4">
            <Button :label="addButtonLabel" @click="addSite" severity="primary" />
            <Button @click="router.push('/postbacks')" label="View Afiliate Links" class="px-3" severity="secondary"
                outlined />
        </div>
        <!-- Your existing sites table -->
        <div class=" justify-center items-center  pt-8">
            <div class="w-full rounded-lg shadow-md">
                <DataTable ref="dataTable" v-model:expandedRows="expandedRows" :value="sites"
                    v-model:editingRows="editingRows" editMode="row" dataKey="id" @row-edit-save="onRowEditSave"
                    @row-edit-cancel="onRowEditCancel" scrollable :scrollHeight="'80vh'" class="w-full ">
                    <Column :frozen="!editingRows || !editingRows.length" alignFrozen="left" expander
                        style="width: 5rem" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>

                    </Column>
                    <Column :frozen="!editingRows || !editingRows.length" alignFrozen="left" field="name" header="Name"
                        :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #editor="{ data, field }">
                            <InputText v-model="data[field]" ref="nameInputs" fluid placeholder="Enter site name" />
                        </template>
                    </Column>
                    <Column style="width: 5rem" header="Links" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            {{ getAdvertisersBySiteId(slotProps.data.id).value.length }}
                        </template>
                    </Column>
                    <Column field="type" header="Type" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Tag :value="slotProps.data.type"
                                :severity="Utils.getSiteTypeSeverity(slotProps.data.type)" />
                        </template>
                        <template #editor="{ data, field }">
                            <Select v-model="data[field]" :options="siteTypes" optionLabel="name" optionValue="code"
                                placeholder="Select a type" fluid />
                        </template>

                    </Column>
                    <Column field="url" header="Domain" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <a class="clickable-link" :href="Utils.formatUrl(data.url)" target="_blank"
                                rel="noopener noreferrer">{{ data.url
                                }}</a>
                        </template>
                        <template v-if="!isLoading" #editor="{ data, field }">
                            <InputText v-model="data[field]" fluid
                                :placeholder="data.type === 'seo' ? 'Enter site url' : 'Optional'" />
                        </template>
                    </Column>
                    <Column field="country" header="Country" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data, field }">
                            <span>{{ getCountryName(data[field]) }}</span>
                            <span :class="marketClass(data[field])" class="ml-2"></span>
                        </template>
                        <template #editor="{ data, field }">
                            <Select filter v-model="data[field]" :options="sortedCountries" optionLabel="name"
                                optionValue="code" placeholder="Select a country" fluid>
                                <template #option="slotProps">
                                    <div class="flex items-center justify-between w-full">
                                        <div class="flex items-center">
                                            <span :class="`fi fi-${slotProps.option.code.toLowerCase()}`" class="mr-2">
                                            </span>
                                            <div>{{ slotProps.option.name }}</div>
                                        </div>
                                        <span v-if="markets.includes(slotProps.option.code)"
                                            class="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                                            Available Deals
                                        </span>
                                    </div>
                                </template>
                            </Select>
                        </template>
                    </Column>
                    <Column v-if="store.getters.isSeo" field="status" header="Status" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Tag :value="slotProps.data.status"
                                :severity="Utils.getSiteStatusSeverity(slotProps.data.status)" />
                        </template>
                    </Column>
                    <Column visible="false" :rowEditor="true" style="width: 10%; min-width: 8rem"
                        bodyStyle="text-align:center" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <div v-if="!isEditing(slotProps.data.id) && slotProps.data.status !== 'approved'">
                                <!-- Edit Button -->
                                <Button icon="pi pi-pencil" rounded text @click="() => onRowEditInit(slotProps.data)"
                                    v-tooltip.bottom="'Edit'" />
                            </div>
                            <!-- Save Button -->
                            <div v-if="isEditing(slotProps.data.id)">
                                <Button icon="pi pi-check" rounded text @click="onRowEditSave(slotProps)"
                                    v-tooltip.bottom="'Save this site'" />

                                <!-- Cancel Button -->
                                <Button icon="pi pi-times" rounded text @click="onRowEditCancel(slotProps.data)"
                                    v-tooltip.bottom="cancelTooltip" />
                            </div>
                        </template>
                    </Column>
                    <template #expansion="slotProps">
                        <DataTable class="ml-18 " v-if="anyAdvertisersBySiteId(slotProps.data.id).value"
                            :value="getAdvertisersBySiteId(slotProps.data.id).value" dataKey="linkId">
                            <Column field="name" header="Advertiser" :headerStyle="nestedHeaderStyles">
                            </Column>
                            <Column field="createdAt" header="Creation Time" :headerStyle="nestedHeaderStyles">
                            </Column>
                            <Column field="deal" header="Deal" :headerStyle="nestedHeaderStyles">
                                <template #body="slotProps">
                                    {{ getFriendlyDealMessage(slotProps.data.deal) }}
                                </template>
                            </Column>
                            <Column field="affiliateLink" header="Affiliation Link (use for traffic)" :headerStyle="nestedHeaderStyles">
                                <template #body="{ data }">
                                    <div class="flex" v-if="data.affiliateLink">
                                        <div class="aff-link cursor-pointer flex items-center gap-2">
                                            <a :href=data.affiliateLink target="_blank"
                                                rel="noopener noreferrer" class="hover:underline">
                                                {{ data.affiliateLink }}
                                            </a>
                                            <Button icon="pi pi-copy" text rounded size="small" severity="secondary"
                                                class="text-gray-500 hover:text-gray-700" v-tooltip.bottom="'Copy link'"
                                                @click.stop="copyToClipboard(data.affiliateLink)" />
                                        </div>
                                    </div>
                                </template>
                            </Column>
                            <Column field="status" header="Link status" :headerStyle="nestedHeaderStyles">
                                <template #body="slotProps">
                                    <Tag :value="slotProps.data.status"
                                        :severity="Utils.getLinkStatusSeverity(slotProps.data.status)" />
                                </template>
                            </Column>
                            <Column :rowEditor="true" style="width: 10%; min-width: 8rem" bodyStyle="text-align:center"
                                :headerStyle="nestedHeaderStyles">
                                <template v-if="isLoading" #body>
                                    <Skeleton></Skeleton>
                                </template>
                            </Column>
                        </DataTable>
                        <div class="flex items-center justify-center h-full" v-else>
                            <h5> Linked Advertisers will appear here</h5>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from "vue";
import { useToast } from "primevue/usetoast";
import SitesService from '@/services/SitesService';
import SiteLinkService from "@/services/SiteLinkService";
import Utils from '@/utils';
import { getSiteTypes } from '@/utils/siteTypes';
import { marketClass } from '@/utils/markets';
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import { downloadCSV } from '@/utils/csv';
import { getFriendlyDealMessage } from '@/utils/deals';
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router';

const store = useStore()
const route = useRoute();
const router = useRouter();
const isSeo = computed(() => store.getters.isSeo);

const addButtonLabel = computed(() => {
    return store.getters.isSeo ? 'Add Site details' : 'Add Toplist'
})

//for non seo users, remove seo from the list
const siteTypes = computed(() => {
    const allTypes = getSiteTypes()
    return store.getters.isSeo
        ? allTypes.filter(type => type.code === 'seo')
        : allTypes.filter(type => type.code !== 'seo')
})

const isEditing = computed(() => {
    return (rowId) => editingRows.value.find(row => row.id === rowId);
});

const onRowEditInit = (rowData) => {
    editingRows.value = [...editingRows.value, rowData];
};

//expansion code
const expandedRows = ref({});

const anyAdvertisersBySiteId = (siteId) => {
    return computed(() => {
        let site = sites.value.find(site => site.id === siteId);

        if (site === null) { false }
        return site.advertisers.length !== 0;
    });
}

const sortAdvertisersByLatestCreatedAt = (advertisers = []) => {
    return [...advertisers].sort((a, b) => {
        const aCreatedAt = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreatedAt = b?.createdAt ? new Date(b.createdAt).getTime() : 0;

        return bCreatedAt - aCreatedAt;
    });
};

//this doesnt perform great when there is a frozen row
const getAdvertisersBySiteId = (siteId) => {
    return computed(() => {
        try {
            let site = sites.value.find(site => site.id === siteId);
            return site.status !== 'approved'
                ? []
                : sortAdvertisersByLatestCreatedAt(
                    site.advertisers.filter(adv => adv.status !== 'deleted')
                );
        } catch (err) {
            console.error(err);
            return [];
        }
    });
};

//end expansion code
const toast = useToast();
const isLoading = ref(true);

let tempRowId = "temp";
const rowAdd = ref(false);
// Computed property for tooltip message
const cancelTooltip = computed(() => {
    return rowAdd.value ? 'Cancel add' : 'Cancel edit';
});

//datatable 
const dataTable = ref(null); // Reference to DataTable
const nameInputs = ref(); // Refs for the name input fields

const editingRows = ref([]);
const sites = ref(new Array(0));

import countries from '@/utils/countries';
import { getCountryName } from '@/utils/countries';
import { markets } from '@/utils/markets';

const sortedCountries = computed(() => {
    // Split countries into two arrays: market countries and others
    const marketCountries = countries.value.filter(country => markets.includes(country.code));
    const otherCountries = countries.value.filter(country => !markets.includes(country.code));

    // Sort each array alphabetically by name
    marketCountries.sort((a, b) => a.name.localeCompare(b.name));
    otherCountries.sort((a, b) => a.name.localeCompare(b.name));

    // Add a divider between market and other countries
    return [
        ...marketCountries,
        ...otherCountries
    ];
});

//a UI method
function addSite() {
    if (!rowAdd.value) {
        editingRows.value = [];
        nextTick(() => {

            rowAdd.value = true;
            const newSite = {
                id: tempRowId,  // Unique ID for the new row
                name: '',
                type: store.getters.isSeo ? 'seo' : '',
                url: '',
                country: '',
                status: 'Pending'
            };
            if (sites.value.length === 0) {
                sites.value = [newSite];
            }
            else {
                sites.value = [newSite, ...sites.value];  // Add the new site to the top of the list
            }
            editingRows.value = [newSite];
            // Programmatically trigger edit mode and focus after render
            setTimeout(() => {
                const inputEl = nameInputs.value;
                if (inputEl) {
                    inputEl.$el.focus(); // Access the DOM element and call focus on it
                }
            }, 100); // 100ms delay, you can adjust this as needed
        })

        //disable add site button ?
    } else {
        console.log('Add already in progress');
        toast.add({ severity: 'warn', summary: 'Add site already in progress', detail: 'Submit current site before adding a new one', life: 3000 });
    }

}

function onRowEditCancel(data) {
    //ensure temp row is removed
    // const { data } = event; // Data being saved (i.e., the row data)
    console.log(data);
    if (data.id === tempRowId) {
        sites.value = sites.value.filter(site => site.id !== tempRowId);
        rowAdd.value = false;
    } else {
        editingRows.value = editingRows.value.filter(row => row.id !== data.id);
    }
}

function validateChanges(data) {
    let validationErrors = [];

    if (!data.type) {
        validationErrors.push('Site type is mandatory');
    }
    if (!data.name) {
        validationErrors.push('Specify a site name');
    }
    if (data.type === 'seo' && !data.url) {
        validationErrors.push('Site url cannot be empty');
    }
    if (!data.country) {
        validationErrors.push('Site country of operation for the site');
    }

    if (validationErrors.length) {
        let errorDetail = "";
        validationErrors.forEach((validationErr) => {
            errorDetail += validationErr + "\n";
        })
        console.log('validation failed');
        toast.add({ severity: 'error', summary: 'Validation failed', detail: errorDetail, life: 5000 });
        editingRows.value = [...editingRows.value, data];
        return false;
    } else
        return true;

}

async function onRowEditSave(event) {
    const { data, index } = event;
    console.log(editingRows.value);

    if (validateChanges(data)) {
        if (data.id === tempRowId) {
            try {
                const response = await SitesService.add(data);
                console.log(response);
                sites.value[index] = response.data;
                rowAdd.value = false;
            } catch (ex) {
                console.log(ex);
                const itemType = store.getters.isSeo ? 'Site' : 'Toplist';
                if (ex.response?.status === 409) {
                    toast.add({
                        severity: 'error',
                        summary: `Duplicate Name`,
                        detail: `${itemType} name already exists, please choose a different name`,
                        life: 3000
                    });
                } else {
                    toast.add({
                        severity: 'error',
                        summary: 'Please try again later',
                        detail: `Could not edit your ${itemType.toLowerCase()} at this time`,
                        life: 3000
                    });
                }
            }
        } else {
            const itemType = store.getters.isSeo ? 'site' : 'toplist';
            toast.add({
                severity: 'error',
                summary: 'Please try again later',
                detail: `Could not edit your ${itemType} at this time`,
                life: 3000
            });
            console.log('add update functionality');
        }
    }
}

async function exportToExcel() {
    try {
        await SiteLinkService.getAllForCsvExport().then((response) => {
            console.log('export', response.data);
            if (response.data.length === 0) {
                forceExportDisabled.value = true;
                toast.add({ severity: 'info', summary: 'No data to export', detail: 'No affiliate links to export', life: 3000 });
            }
            else {

                downloadCSV(response.data, 'sites.csv');

            }
        });
    } catch (err) {
        console.error(err);
    }
}

const isExportDisabled = computed(() => {
    console.log(sites.value);
    return sites.value.length === 0 || sites.value.filter(sites => sites.status === 'approved').length === 0 || forceExportDisabled.value;
});

const tooltipMessage = computed(() => {
    return isExportDisabled.value ? forceExportDisabled.value ? 'No affiliate links exist' : 'No approved sites yet' : 'Export data to excel';
});

const forceExportDisabled = ref(false);

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text)
        toast.add({
            severity: 'success',
            summary: 'Copied!',
            detail: 'Copied to clipboard',
            life: 3000
        })
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy to clipboard',
            life: 3000
        })
    }
}

async function fetchSites() {
    try {
        await SitesService.getUserSitesWithLinks().then((response) => {
            sites.value = response.data;
            isLoading.value = false;
        });
    } catch (err) {
        console.error(err);
    } finally {
    }
}

onMounted(async () => {
    await fetchSites();
});

</script>

<style scoped>
.clickable-link {
    color: white;
    text-decoration: underline;
}

.aff-link {
    color: rgb(213, 179, 40);
    text-decoration: underline;
}
</style>
